import {
    useEffect,
    useState,
    useRef,
    type FC
} from "react";
import {
    type NativeScrollEvent,
    type LayoutChangeEvent,
    ScrollView,
    Platform,
    View
} from "react-native";
import type IMarkdownEditorToolbarProps from "./type";
import type {
    MarkdownEditorToolbarWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    StrikethroughIcon,
    AlignCenterIcon,
    ListOrderedIcon,
    AlignRightIcon,
    AlignLeftIcon,
    UnderlineIcon,
    ItalicIcon,
    Undo2Icon,
    Redo2Icon,
    QuoteIcon,
    ImageIcon,
    LinkIcon,
    CodeIcon,
    BoldIcon,
    ListIcon
} from "./icons";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    MarkdownEditorInlineFormat,
    MarkdownEditorListKind
} from "../../type";
import {
    applyMarkdownEditorToolbarTabStop,
    getMarkdownEditorToolbarItems,
    getMarkdownEditorHTMLElement
} from "../../util";
import EdgeFade from "../../../scrollArea/internal/edgeFade";
import MarkdownEditorHeadingSelect from "../headingSelect";
import ToggleGroup from "../../../toggleGroup";
import Button from "../../../button";
import Toggle from "../../../toggle";

const INLINE_GROUP_FORMATS: Array<MarkdownEditorInlineFormat> = [
    "strikethrough",
    "underline",
    "italic",
    "bold"
];

const getShortcutProps = (shortcut: string): MarkdownEditorToolbarWebPropsType => {
    return Platform.OS === "web" ? {
        "aria-keyshortcuts": shortcut
    } : {};
};

const MarkdownEditorToolbar: FC<IMarkdownEditorToolbarProps> = ({
    isAtBottom = false,
    isDisabled = false,
    onTextStyleChange,
    customLocalize,
    onInlineToggle,
    returnFocusRef,
    activeFormats,
    onQuoteToggle,
    onListToggle,
    customTheme,
    variants,
    canRedo,
    canUndo,
    onAlign,
    onImage,
    onLink,
    onRedo,
    onUndo,
    style
}) => {
    const {
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const [
        scrollMetrics,
        setScrollMetrics
    ] = useState({
        contentWidth: 0,
        layoutWidth: 0,
        offset: 0
    });

    const toolbarRef = useRef<View>(null);

    const lastFocusedRef = useRef<HTMLElement | null>(null);

    const isCoarse = pointerType === "coarse";

    const size = isCoarse ? "large" : "small";

    const {
        container: containerDynamicStyle,
        separator: separatorDynamicStyle,
        items: itemsDynamicStyle,
        fade: fadeDynamicStyle
    } = useStyles({
        isAtBottom,
        iconSizes,
        borders,
        colors,
        spaces
    });

    useEffect(() => {
        const element = getMarkdownEditorHTMLElement(toolbarRef.current);

        if(Platform.OS !== "web" || !element) {
            return;
        }

        applyMarkdownEditorToolbarTabStop(element, lastFocusedRef.current);
    });

    useEffect(() => {
        const element = getMarkdownEditorHTMLElement(toolbarRef.current);

        if(Platform.OS !== "web" || !element) {
            return;
        }

        const getItemIndex = (items: Array<HTMLElement>, target: EventTarget | null) => {
            return items.findIndex((item) => target instanceof Node && item.contains(target));
        };

        const onFocusIn = (event: FocusEvent) => {
            const items = getMarkdownEditorToolbarItems(element);

            const item = items[getItemIndex(items, event.target)];

            if(!item) {
                return;
            }

            lastFocusedRef.current = item;

            applyMarkdownEditorToolbarTabStop(element, item);
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if(event.isComposing || event.altKey || event.ctrlKey || event.metaKey) {
                return;
            }

            const items = getMarkdownEditorToolbarItems(element);

            const index = getItemIndex(items, event.target);

            const item = items[index];

            if(!item) {
                return;
            }

            if(event.key === "ArrowDown" && item.getAttribute("aria-haspopup") === "listbox") {
                event.preventDefault();

                item.click();

                return;
            }

            const forward = direction === "rtl" ? "ArrowLeft" : "ArrowRight";

            const backward = direction === "rtl" ? "ArrowRight" : "ArrowLeft";

            const nextIndexes: Record<string, number> = {
                [backward]: (index - 1 + items.length) % items.length,
                [forward]: (index + 1) % items.length,
                End: items.length - 1,
                Home: 0
            };

            const nextIndex = nextIndexes[event.key];

            if(nextIndex === undefined) {
                return;
            }

            event.preventDefault();

            event.stopPropagation();

            items[nextIndex]?.focus();
        };

        element.addEventListener("focusin", onFocusIn);

        element.addEventListener("keydown", onKeyDown, true);

        return () => {
            element.removeEventListener("focusin", onFocusIn);

            element.removeEventListener("keydown", onKeyDown, true);
        };
    }, [
        direction
    ]);

    const handleScroll = ({
        nativeEvent
    }: {
        nativeEvent: NativeScrollEvent;
    }) => {
        const offset = nativeEvent.contentOffset.x;

        setScrollMetrics((current) => Math.abs(current.offset - offset) < 1 ? current : {
            ...current,
            offset
        });
    };

    const handleContentSizeChange = (contentWidth: number) => {
        setScrollMetrics((current) => current.contentWidth === contentWidth ? current : {
            ...current,
            contentWidth
        });
    };

    const handleScrollLayout = ({
        nativeEvent
    }: LayoutChangeEvent) => {
        const layoutWidth = nativeEvent.layout.width;

        setScrollMetrics((current) => current.layoutWidth === layoutWidth ? current : {
            ...current,
            layoutWidth
        });
    };

    const maxOffset = scrollMetrics.contentWidth - scrollMetrics.layoutWidth;

    const isLeftFadeVisible = scrollMetrics.offset > 1;

    const isRightFadeVisible = maxOffset > 1 && scrollMetrics.offset < maxOffset - 1;

    const handleInlineChange = (value: string | Array<string>) => {
        const next = Array.isArray(value) ? value : [
            value
        ];

        const current = activeFormats.inline;

        const changed = INLINE_GROUP_FORMATS.find((format) => next.includes(format) !== current.includes(format));

        if(changed) {
            onInlineToggle(changed);
        }
    };

    const handleListChange = (value: string | Array<string>) => {
        const next = Array.isArray(value) ? value[0] : value;

        const kind: MarkdownEditorListKind | undefined = next === "bullet" || next === "ordered" ? next : activeFormats.list ?? undefined;

        if(kind) {
            onListToggle(kind);
        }
    };

    const renderSeparator = () => {
        return <View
            style={[
                stylesheet.separator,
                separatorDynamicStyle
            ]}
            aria-hidden={true}
        />;
    };

    const renderFade = (isLeft: boolean) => {
        if(!(isLeft ? isLeftFadeVisible : isRightFadeVisible)) {
            return null;
        }

        return <EdgeFade
            edge={isLeft === (direction === "ltr") ? "start" : "end"}
            color={colors.content.container.raised}
            style={[
                stylesheet.fade,
                fadeDynamicStyle
            ]}
        />;
    };

    const renderAlign = () => {
        if(!onAlign) {
            return null;
        }

        const alignItems = [
            {
                title: localize("markdown-editor-align-left"),
                icon: AlignLeftIcon,
                alignment: "left"
            },
            {
                title: localize("markdown-editor-align-center"),
                icon: AlignCenterIcon,
                alignment: "center"
            },
            {
                title: localize("markdown-editor-align-right"),
                icon: AlignRightIcon,
                alignment: "right"
            }
        ] as const;

        return <>
            {renderSeparator()}
            {
                alignItems.map((item) => <Button
                    customIconColor={colors.content.icon.mid}
                    onPress={() => onAlign(item.alignment)}
                    accessibilityLabel={item.title}
                    customTheme={customTheme}
                    isDisabled={isDisabled}
                    key={item.alignment}
                    icon={item.icon}
                    variant="ghost"
                    type="neutral"
                    size={size}
                />)
            }
        </>;
    };

    const renderImage = () => {
        if(!onImage) {
            return null;
        }

        return <Button
            accessibilityLabel={localize("markdown-editor-image")}
            customIconColor={colors.content.icon.mid}
            customTheme={customTheme}
            isDisabled={isDisabled}
            onPress={onImage}
            icon={ImageIcon}
            variant="ghost"
            type="neutral"
            size={size}
        />;
    };

    const renderItems = () => {
        return <>
            <MarkdownEditorHeadingSelect
                returnFocusRef={returnFocusRef}
                customLocalize={customLocalize}
                value={activeFormats.textStyle}
                onChange={onTextStyleChange}
                customTheme={customTheme}
                isDisabled={isDisabled}
                variants={variants}
                size={size}
            />
            {renderSeparator()}
            <ToggleGroup
                value={activeFormats.inline.filter((format) => INLINE_GROUP_FORMATS.includes(format))}
                title={localize("markdown-editor-text-format")}
                onValueChange={handleInlineChange}
                selectBehaviour="multiple"
                customTheme={customTheme}
                isDisabled={isDisabled}
                size={size}
            >
                <ToggleGroup.Item
                    {...getShortcutProps("Control+B")}
                    title={localize("markdown-editor-bold")}
                    isIconOnly={true}
                    icon={BoldIcon}
                    value="bold"
                />
                <ToggleGroup.Item
                    {...getShortcutProps("Control+I")}
                    title={localize("markdown-editor-italic")}
                    icon={ItalicIcon}
                    isIconOnly={true}
                    value="italic"
                />
                <ToggleGroup.Item
                    {...getShortcutProps("Control+U")}
                    title={localize("markdown-editor-underline")}
                    icon={UnderlineIcon}
                    isIconOnly={true}
                    value="underline"
                />
                <ToggleGroup.Item
                    title={localize("markdown-editor-strikethrough")}
                    icon={StrikethroughIcon}
                    value="strikethrough"
                    isIconOnly={true}
                />
            </ToggleGroup>
            {renderSeparator()}
            <ToggleGroup
                title={localize("markdown-editor-list")}
                value={activeFormats.list ?? []}
                onValueChange={handleListChange}
                customTheme={customTheme}
                isDisabled={isDisabled}
                size={size}
            >
                <ToggleGroup.Item
                    title={localize("markdown-editor-bullet-list")}
                    isIconOnly={true}
                    icon={ListIcon}
                    value="bullet"
                />
                <ToggleGroup.Item
                    title={localize("markdown-editor-numbered-list")}
                    icon={ListOrderedIcon}
                    isIconOnly={true}
                    value="ordered"
                />
            </ToggleGroup>
            {renderSeparator()}
            <Button
                {...getShortcutProps("Control+K")}
                accessibilityLabel={localize("markdown-editor-link")}
                customIconColor={colors.content.icon.mid}
                customTheme={customTheme}
                isDisabled={isDisabled}
                onPress={onLink}
                icon={LinkIcon}
                variant="ghost"
                type="neutral"
                size={size}
            />
            <Toggle
                isPressed={activeFormats.inline.includes("code")}
                onPressedChange={() => onInlineToggle("code")}
                title={localize("markdown-editor-code")}
                customTheme={customTheme}
                isDisabled={isDisabled}
                isIconOnly={true}
                icon={CodeIcon}
                size={size}
            />
            <Toggle
                title={localize("markdown-editor-quote")}
                isPressed={activeFormats.isQuote}
                onPressedChange={onQuoteToggle}
                customTheme={customTheme}
                isDisabled={isDisabled}
                isIconOnly={true}
                icon={QuoteIcon}
                size={size}
            />
            {renderImage()}
            {renderAlign()}
            {renderSeparator()}
            <Button
                {...getShortcutProps("Control+Z")}
                accessibilityLabel={localize("markdown-editor-undo")}
                customIconColor={colors.content.icon.mid}
                isDisabled={isDisabled || !canUndo}
                customTheme={customTheme}
                icon={Undo2Icon}
                onPress={onUndo}
                variant="ghost"
                type="neutral"
                size={size}
            />
            <Button
                {...getShortcutProps("Control+Shift+Z")}
                accessibilityLabel={localize("markdown-editor-redo")}
                customIconColor={colors.content.icon.mid}
                isDisabled={isDisabled || !canRedo}
                customTheme={customTheme}
                icon={Redo2Icon}
                onPress={onRedo}
                variant="ghost"
                type="neutral"
                size={size}
            />
        </>;
    };

    return <View
        aria-label={localize("markdown-editor-toolbar")}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        ref={toolbarRef}
        role="toolbar"
    >
        <View
            style={stylesheet.frame}
        >
            <ScrollView
                onContentSizeChange={handleContentSizeChange}
                showsHorizontalScrollIndicator={false}
                keyboardShouldPersistTaps="always"
                onLayout={handleScrollLayout}
                contentContainerStyle={[
                    stylesheet.scrollItems,
                    itemsDynamicStyle
                ]}
                scrollEventThrottle={16}
                onScroll={handleScroll}
                horizontal={true}
            >
                {renderItems()}
            </ScrollView>
            {renderFade(true)}
            {renderFade(false)}
        </View>
    </View>;
};
export default MarkdownEditorToolbar;
